import React, {useState, useCallback, useRef, useMemo, useEffect} from 'react'
import {toast} from 'react-toastify'
import {useDispatch, useSelector} from 'react-redux'

import {Modal} from '@thryvlabs/maverick'
import {Input} from '../../shared/Input'
import {Button} from '../../shared/Button'
import {SHOW_INVOICE_INFO_FOR_PAST_DUE} from '../../../utils/past-due-invoice'
import {InvoiceInformation} from '../../../components/account-information-components/invoice-information'

import {
  validateEmail,
  validateEmailWithApi,
  getApiErrorMessage,
  OUTCOME,
  EMAIL_FOOTER_CONFIG,
} from './EmailLookup.utils'

import {useAuthClient} from '../../../utils/use-auth-client'
import {useAsync} from '../../../utils/use-async'

import {
  goNext,
  resetJourney,
  selectCompletedSteps,
  selectEmailLookupState,
  setEmailLookupState,
  setViewResultsEmail,
} from '../../../app-store/AccountSetupSlice'

import {
  Container,
  Header,
  Title,
  Subtitle,
  InputRow,
  InputFill,
  SearchButtonWrapper,
  StatusLine,
  StatusIcon,
  FooterRow,
  FooterCenter,
  InputRowWrapper,
} from './EmailLookup.styles'

import {selectEffectiveRole} from '../../../app-store/selector'
import {useFlags} from 'launchdarkly-react-client-sdk'
import {useUserFeatures} from '../../../context/user-features-context'
import {StyledSearchIcon} from '../../shared/icons/icons.styles'
import {
  resetRecurlyAccountInfo,
  setRecurlyAccountInfo,
  updateProposalInfo,
} from '../../../app-store/ProposalSlice'

// ── Footer ────────────────────────────────────────────────────────────────
// email-lookup step → outcome-driven buttons (no standard Continue)
// all other steps   → standard Back / Continue
const RenderFooter = ({canGoBack, canProceed, flowType}) => {
  const dispatch = useDispatch()
  const emailLookupState = useSelector(selectEmailLookupState)
  const completedSteps = useSelector(selectCompletedSteps)

  // Footer render removed as we will have different ui for proposal component
  if (flowType === 'proposal') {
    return <></>
  }

  // ── Navigation ────────────────────────────────────────────────────────────

  const handleEmailLookupNavigation = id => {
    if (id === 'proceedToCreateAccount') {
      dispatch(goNext())
      return
    }
    if (id === 'addLocation') {
      dispatch(setViewResultsEmail(emailLookupState?.verifiedEmail))
      return
    }
  }

  const emailFooterBtns =
    EMAIL_FOOTER_CONFIG[emailLookupState?.accountType] ?? []
  if (completedSteps?.includes('email-lookup')) {
    return null
  }
  return (
    <FooterRow>
      {/* Outcome buttons only appear after a successful lookup */}
      {emailFooterBtns.length > 0 && (
        <FooterCenter>
          {emailFooterBtns.map(btn => (
            <Button
              key={btn.id}
              variant="solid-gradiant"
              size="md"
              fullWidth="true"
              onClick={() => handleEmailLookupNavigation(btn.id)}
            >
              {btn.label}
            </Button>
          ))}
        </FooterCenter>
      )}
    </FooterRow>
  )
}

// ─── Status line config ───────────────────────────────────────────────────────

const STATUS_CONFIG = {
  [OUTCOME.NEW_CUSTOMER]: {
    variant: 'not-found',
    message: (
      <>
        ! Email not found — <strong>New customer</strong>
      </>
    ),
    role: 'status',
  },

  [OUTCOME.EXISTING_CUSTOMER]: {
    variant: 'found',
    message: (
      <>
        ! Email found — <strong>Existing customer</strong>
      </>
    ),
    role: 'status',
  },

  [OUTCOME.UPGRADE_CUSTOMER]: {
    variant: 'found',
    message: (
      <>
        ! Email found — <strong>Existing customer</strong>
      </>
    ),
    role: 'status',
  },

  [OUTCOME.PARTNER_MISALIGNED]: {
    variant: 'error',
    message:
      'Oops! Looks like we already have this email registered. Please reach out to your PSM to confirm enrollment/purchase.',
    role: 'alert',
  },

  [OUTCOME.PARTNER_OWNED_ACCOUNT]: {
    variant: 'found',
    message: (
      <>
        ! Email found — <strong>Existing customer</strong>
      </>
    ),
    role: 'status',
  },

  [OUTCOME.MULTIPLE_PARENT]: {
    variant: 'multiple-accounts',
    message: (
      <>
        ! Email found — <strong>Multiple accounts</strong>
      </>
    ),
    role: 'status',
  },
}

const INFLUENCER_OPS_PARTNER_MISALIGNED_MESSAGE = (
  <>
    Oops! Wrong lane.
    <br />
    Looks like this email is registered as a Partner Account. Please contact
    your OOE Support Team for more details and next steps.
  </>
)

const toRoleList = roles =>
  Array.isArray(roles) ? roles : roles ? [roles] : []

const hasInfluencerOpsRole = roles =>
  toRoleList(roles).some(role => String(role).toLowerCase() === 'influencerops')

const getStatusConfig = (status, roles) => {
  const config = STATUS_CONFIG[status]

  if (status !== OUTCOME.PARTNER_MISALIGNED || !hasInfluencerOpsRole(roles)) {
    return config
  }

  return {
    ...config,
    message: INFLUENCER_OPS_PARTNER_MISALIGNED_MESSAGE,
  }
}

export function EmailLookup({
  onReady,
  keapPartnerId: keapPartnerIdProp,
  handleEmailResponse,
  flowType = '',
}) {
  const {run} = useAsync({status: 'pending'})
  const client = useAuthClient()
  const dispatch = useDispatch()

  const roles = useSelector(selectEffectiveRole)
  const keapPartnerId = keapPartnerIdProp ?? null

  const {frontendInvoiceCc, frontendOverrideSst, frontendUserError} = useFlags()

  const {Enable_BNI_Leads_Lift, Enable_BNI_Social_Ads} = useUserFeatures()

  const featureFlags = useMemo(
    () => ({
      frontendInvoiceCc,
      frontendOverrideSst,
      frontendUserError,
      Enable_BNI_Leads_Lift,
      Enable_BNI_Social_Ads,
    }),
    [
      frontendInvoiceCc,
      frontendOverrideSst,
      frontendUserError,
      Enable_BNI_Leads_Lift,
      Enable_BNI_Social_Ads,
    ],
  )

  const completedSteps = useSelector(selectCompletedSteps)
  const emailLookupState = useSelector(selectEmailLookupState)

  const [email, setEmail] = useState(emailLookupState?.verifiedEmail || '')
  const [status, setStatus] = useState('idle')
  const [fieldError, setFieldError] = useState(null)
  const [showPastDueInvoiceModal, setShowPastDueInvoiceModal] = useState(false)
  const [pastDueInvoiceContext, setPastDueInvoiceContext] = useState(null)

  const inputRef = useRef(null)

  useEffect(() => {
    if (
      completedSteps.includes('email-lookup') &&
      emailLookupState?.verifiedEmail
    )
      setEmail(emailLookupState?.verifiedEmail)
  }, [emailLookupState, completedSteps])

  // ── Handle special outcomes ────────────────────────────────────────────────

  const handleOutcome = useCallback((outcome, meta, lookedUpEmail) => {
    switch (outcome) {
      case OUTCOME.INVOICE_BILLED:
        setPastDueInvoiceContext({thryvId: meta?.thryvId, email: lookedUpEmail})
        setShowPastDueInvoiceModal(true)
        break

      // ── Keap partner blocking outcomes ────────────────────────────────────
      case OUTCOME.EXISTING_CUSTOMER:
      case OUTCOME.MIX_SUBSCRIPTION:
      case OUTCOME.BNI_RESTRICTED:
      case OUTCOME.SOCIAL_ADS_BNI_RESTRICTED:
        toast.error(
          'Oops! Looks like we already have this email address registered. Please reach out to your Partner representative to confirm enrollment/purchase.',
        )
        break

      default:
        break
    }
  }, [])

  // ── Search ────────────────────────────────────────────────────────────────

  const handleSearch = useCallback(async () => {
    dispatch(resetJourney())
    const trimmedEmail = email.trim()

    const validationError = validateEmail(trimmedEmail, roles)

    if (validationError) {
      setFieldError(validationError)
      inputRef.current?.focus()
      return
    }

    setFieldError(null)
    setStatus('loading')

    try {
      const {outcome, meta} = await validateEmailWithApi({
        email: trimmedEmail,
        run,
        client,
        featureFlags,
        keapPartnerId,
      })

      setStatus(outcome)
      handleOutcome(outcome, meta, trimmedEmail)
      handleEmailResponse?.({outcome, meta})
      if (flowType === 'proposal') {
        dispatch(setRecurlyAccountInfo(meta?.subDetails ?? {}))
        dispatch(
          updateProposalInfo({
            clientEmail: trimmedEmail,
            thryvId: meta?.thryvId ?? null,
          }),
        )
      }

      dispatch(
        setEmailLookupState({
          email: trimmedEmail,
          accountType: outcome,
          meta,
        }),
      )

      if (
        outcome === OUTCOME.NEW_CUSTOMER ||
        outcome === OUTCOME.UPGRADE_CUSTOMER
      ) {
        onReady?.(true)
        return
      }

      onReady?.(false)
    } catch (err) {
      setStatus('idle')
      toast.error(getApiErrorMessage(err, featureFlags.frontendUserError))
    }
  }, [
    email,
    roles,
    run,
    client,
    featureFlags,
    keapPartnerId,
    dispatch,
    onReady,
    handleOutcome,
    handleEmailResponse,
  ])

  // ── Input handlers ────────────────────────────────────────────────────────

  const handleChange = e => {
    const value = e.target.value

    setEmail(value)

    if (fieldError) {
      setFieldError(null)
    }

    // if (status !== 'idle') {
    setStatus('idle')
    onReady?.(false)
    handleEmailResponse?.({outcome: 'idle', meta: {}})
    dispatch(resetJourney())
    // }
  }

  const handleClear = () => {
    setEmail('')
    setStatus('idle')
    setFieldError(null)

    onReady?.(false)
    handleEmailResponse?.({outcome: 'idle', meta: {}})
    dispatch(resetJourney())

    if (flowType === 'proposal') {
      dispatch(resetRecurlyAccountInfo())
    }

    inputRef.current?.focus()
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const statusConfig = getStatusConfig(status, roles)

  return (
    <Container>
      <Header>
        {flowType === 'proposal' ? (
          <>
            <Title>Search Client Email Address</Title>
          </>
        ) : (
          <>
            <Title>Email Lookup</Title>
            <Subtitle>Check if the customer email exists</Subtitle>
          </>
        )}
      </Header>

      <InputRowWrapper>
        <InputRow>
          <InputFill>
            <Input
              ref={inputRef}
              variant="form"
              size="md"
              // label="Email"
              type="email"
              placeholder="Enter email"
              value={email}
              errorText={fieldError}
              onChange={handleChange}
              onKeyDown={e => {
                if (e.key === 'Enter' && status !== 'loading') {
                  handleSearch()
                }
              }}
              disabled={status === 'loading'}
              rightIcon={email ? '×' : null}
              leftIcon={<StyledSearchIcon />}
              onRightIconClick={email ? handleClear : undefined}
            />
          </InputFill>

          <SearchButtonWrapper>
            <Button
              variant="secondary-blue"
              size="md"
              isLoading={status === 'loading'}
              onClick={handleSearch}
            >
              Search
            </Button>
          </SearchButtonWrapper>
        </InputRow>
        {statusConfig && (
          <StatusLine variant={statusConfig.variant} role={statusConfig.role}>
            <StatusIcon />
            <span>{statusConfig.message}</span>
          </StatusLine>
        )}
      </InputRowWrapper>
      <RenderFooter flowType={flowType} />
      {showPastDueInvoiceModal &&
        (SHOW_INVOICE_INFO_FOR_PAST_DUE ? (
          <Modal
            altBtn={<></>}
            openOnLoad
            variant="lg"
            hideX
            onClose={() => setShowPastDueInvoiceModal(false)}
          >
            <InvoiceInformation
              thryvId={pastDueInvoiceContext?.thryvId}
              email={pastDueInvoiceContext?.email}
              selectedCountry={undefined}
              setIsInvoiceBilled={() => setShowPastDueInvoiceModal(false)}
            />
          </Modal>
        ) : (
          <Modal
            altBtn={<></>}
            openOnLoad
            variant="lg"
            footer
            action
            btnAction={() => setShowPastDueInvoiceModal(false)}
            onClose={() => setShowPastDueInvoiceModal(false)}
            btnActionText="Ok"
            hideX
          >
            <div style={{padding: '20px', textAlign: 'center'}}>
              <strong>This account has a past due invoice.</strong>
              <br></br>
              Please resolve the outstanding balance before processing a new
              order.
            </div>
          </Modal>
        ))}
    </Container>
  )
}
